import { ButtonBase, Checkbox, Chip, Typography } from '@material-ui/core'
import React from 'react'

import SLAChip from '../../components/SLAChip'
import {
  assignmentLabel,
  formatWhen,
  isTerminal,
  stateTone,
  ticketStateLabel,
  Ticket
} from '../../helpers/ticketWorkflow'
import { getTranslate } from '../../localization'
import TicketPriority from './TicketPriority'
import useStyles from './styles/SupportTicketRowMobile.styles'

interface SupportTicketRowMobileProps {
  ticket: Ticket
  onClick: (ticket: Ticket) => void
  selected: boolean
  onToggleSelected: (ticket: Ticket) => void
  selectionDisabled?: boolean
}

function SupportTicketRowMobile({
  ticket,
  onClick,
  selected,
  onToggleSelected,
  selectionDisabled
}: SupportTicketRowMobileProps) {
  const classes = useStyles()

  return (
    // The checkbox sits OUTSIDE the ButtonBase, not inside it. Nested inside,
    // every tap toggling selection would also navigate to the ticket, because
    // the click bubbles to the enclosing button — and stopPropagation on a
    // checkbox is the kind of fix that works with a mouse and strands keyboard
    // and screen-reader users, who reach the two controls separately anyway.
    //
    // Selection exists on mobile at all because this file's own rule is
    // "duplicate LAYOUT, never behaviour". Dropping it here would make bulk
    // assignment a desktop-only capability, which is a behaviour difference
    // wearing a layout excuse.
    <div className={classes.selectableRow}>
      <Checkbox
        size="small"
        color="primary"
        checked={selected}
        onChange={() => onToggleSelected(ticket)}
        disabled={selectionDisabled}
        inputProps={{
          'aria-label': getTranslate('Select {number}', {
            number: ticket.ticketNumber
          })
        }}
      />
      <ButtonBase
        className={classes.root}
        style={{ opacity: isTerminal(ticket.state) ? 0.7 : 1 }}
        aria-label={`${ticket.ticketNumber}: ${ticket.title}`}
        onClick={() => onClick(ticket)}>
        <div className={classes.primary}>
          <Typography variant="body2" color="textSecondary">
            <bdi dir="ltr">{ticket.ticketNumber}</bdi>
          </Typography>
          <Typography variant="subtitle2">{ticket.title}</Typography>
          <Typography variant="body2" color="textSecondary">
            {ticket.affectedEntityRefs.length
              ? ticket.affectedEntityRefs
                  .map((reference) => `${reference.type}:${reference.code}`)
                  .map((reference) => (
                    <bdi dir="ltr" key={reference}>
                      {reference}
                    </bdi>
                  ))
                  .reduce<React.ReactNode[]>(
                    (items, reference, index) =>
                      index === 0 ? [reference] : [...items, ' · ', reference],
                    []
                  )
              : getTranslate('No affected entity')}
          </Typography>
        </div>
        <div className={classes.secondary}>
          <TicketPriority priority={ticket.priority} />
          <Chip
            size="small"
            label={ticketStateLabel(ticket.state)}
            color={stateTone(ticket.state)}
          />
          <SLAChip ticket={ticket} />
          <Typography variant="body2" color="textSecondary">
            {assignmentLabel(ticket)}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {formatWhen(ticket.changedAt)}
          </Typography>
        </div>
      </ButtonBase>
    </div>
  )
}

export default React.memo(SupportTicketRowMobile)
